<script>
  import OverpanelControl from '../OverpanelControl/OverpanelControl.svelte';
  import StyledRadioButtonLabel from '../StyledRadioButtonLabel/StyledRadioButtonLabel.svelte';
  import { overpanelMap, closeOverpanel } from '../../stores/overpanelControl';
  import {
    drug,
    drugData,
    setQuantity,
    clearCustomQuantity,
    quantityValue,
    customQuantityValue,
  } from '../../stores/drug';

  let {
    title,
    inputLabel,
    inputPlaceholder,
    updateButtonText,
    customQuantityButtonText,
    dataTestId = 'drug-quantity-overpanel',
    closeCallback = () => {},
  } = $props();

  let input = $state();

  const isSelected = ({ value, isCustomQuantity }) => {
    if ($drugData.quantity && $drugData.quantity.value === value) {
      return (isCustomQuantity && $quantityValue === 'custom') || (!isCustomQuantity && $quantityValue !== 'custom');
    }
    return false;
  };

  let options = $derived($drug ?
    [...$drugData.strength.quantity, $drugData.customQuantity].map((option) => {
      option.selected = isSelected(option);
      return option;
    }) : []);

  let showCustomQuantityInput = $state(false);
  let previousNonCustomOptions = $derived(
    options.find((o) => o.selected && !o.isCustomQuantity)
  );

  quantityValue.subscribe((value) => {
    if (value !== 'custom') {
      showCustomQuantityInput = false;
    }
  });

  const onChange = (e) => {
    if (e.target.value === 'custom') {
      setQuantity($drugData.customQuantity);
    } else {
      showCustomQuantityInput = false;
      setQuantity(options.find((option) => option.value === e.target.value));
    }
  };

  const handleOnMouseUp = (e) => {
    onChange(e);
    if (e.target.value === 'custom') {
      if (!showCustomQuantityInput) {
        showCustomQuantityInput = true;
        window.setTimeout(() => {
          if (input) input.focus();
        }, 20);
      }
    } else {
      clearCustomQuantity();
      closeOverpanel('drugQuantity');
      handleClose();
    }
  };

  const handleKeyPress = (e) => {
    if (e.keyCode === 13) handleOnMouseUp(e);
  };

  const validCustomQuantity = () => {
    const parser = $drugData.strength.package ? parseInt : parseFloat;
    return parser($customQuantityValue) || '';
  };

  const handleInput = (e) => {
    const intValidator = (value) => value === '' || value.match(/^[1-9]\d*$/);
    const floatValidator = (value) =>
      value.match(/^[1-9]\d*\.?\d*$/) || value.match(/^0?\.?$/) || value.match(/^0?\.\d+$/);
    // only allow integer for packaged drug, otherwise, allow float
    const valid = ($drugData.strength.package ? intValidator : floatValidator)(e.target.value);
    if (!valid) {
      // reset to old value
      input.value = $customQuantityValue;
    } else {
      // save new value to store
      $customQuantityValue = input.value;
    }
  };

  const handleBlur = () => {
    // custom input done
    if (options.find((o) => o.selected).isCustomQuantity) {
      //  parse to display valid custom quantity or ''
      $customQuantityValue = validCustomQuantity();
    }
  };

  const handleClose = () => {
    // if using custom and it is invalid
    if (options.find((o) => o.selected).isCustomQuantity && !validCustomQuantity()) {
      // use previous valid option
      setQuantity(previousNonCustomOptions);
      // reset custom quantity
      showCustomQuantityInput = false;
    }
    closeCallback();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    closeOverpanel('drugCustomQuantity');
    closeOverpanel('drugQuantity');
    handleClose();
  };

  const mountCallback = () => {
    if ($customQuantityValue) {
      showCustomQuantityInput = true;
    }
    input.focus();
  };
</script>

{#if $overpanelMap.has('drugQuantity') && $drug}
  <OverpanelControl {title} closeCallback={handleClose} {mountCallback} overpanelKey="drugQuantity">
    <form class="flex flex-col min-h-full bg-gray-mercury" onsubmit={handleSubmit}>
      <fieldset data-testid={dataTestId} onchange={onChange}>
        <legend class="visually-hidden">{title}</legend>
        <div class="flex flex-row flex-wrap">
          {#each options as { label, value, selected, isCustomQuantity }, i}
            <StyledRadioButtonLabel
              id={`quantity-${i}`}
              value={isCustomQuantity ? 'custom' : value}
              {selected}
              onMouseUp={handleOnMouseUp}
              onKeyPress={handleKeyPress}
            >
              {isCustomQuantity ? customQuantityButtonText : label}
            </StyledRadioButtonLabel>
          {/each}
        </div>
      </fieldset>
      <div
        class="flex flex-col justify-between flex-1 transition-all duration-150 ease-in overflow-hidden"
        class:max-h-0={!showCustomQuantityInput}
        data-testid="{dataTestId}-custom-quantity-container"
      >
        <div class="p-4">
          <div class="flex flex-row justify-between mb-1">
            <div id="custom-quantity-input-label" class="font-medium">{inputLabel}</div>
            <div id="custom-quantity-unit-label">{$drugData.strength.unitLabel}</div>
          </div>
          <input
            id="custom-quantity-input"
            aria-labelledby="custom-quantity-input-label custom-quantity-unit-label"
            data-testid="{dataTestId}-custom-quantity-input"
            type="text"
            inputmode="numeric"
            class="w-full shadow-md rounded border px-4 py-2 bg-white text-base leading-loose placeholder-gray-bluey
            focus:outline-none focus:shadow-focus"
            bind:this={input}
            value={$customQuantityValue}
            oninput={handleInput}
            onblur={handleBlur}
            placeholder={inputPlaceholder}
          />
        </div>
        <button
          type="submit"
          class="leading-loose text-center font-medium text-black bg-blue-robin-egg hover-hover:hover:bg-blue-light active:bg-blue-malibu
          focus:outline-none focus:shadow-inset-focus active:shadow-inset-active sm:rounded-md whitespace-nowrap min-w-25 px-8 flex items-center
          justify-center h-16 sm:h-12 mt-0.5 mx-auto sm:max-w-sm w-full sm:my-4"
        >
          {updateButtonText}
        </button>
      </div>
    </form>
  </OverpanelControl>
{/if}
